import {
  Typography,
  Menu,
  MenuItem,
  TextField,
  Button,
} from "@avenue-8/sidekick-ui";
import { SideBySideComparisonSectionConfig } from "@cma/presentation-generation-logic";
import styled from "@emotion/styled";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Highlight lives on a ::before with negative inset so hover doesn't add real padding.
const FieldContainer = styled.div<{ $editMode?: boolean }>`
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    position: absolute;
    inset: -0.375rem;
    z-index: -1;
    border-radius: 0.375rem;
    background-color: transparent;
    pointer-events: none;
    transition: background-color 0.15s ease-in-out;
  }

  &:hover {
    cursor: ${(p) => (p.$editMode ? "pointer" : "default")};
  }

  &:hover::before {
    background-color: ${(p) =>
      p.$editMode ? p.theme.colors.background3 : "transparent"};
  }
`;

const ValueText = styled(Typography)<{
  $clampLines?: number;
  $isPlaceholder?: boolean;
}>`
  align-self: stretch;
  white-space: pre-line;
  ${(p) =>
    p.$clampLines
      ? `
    line-height: 1.375rem;
    height: ${p.$clampLines * 1.375}rem;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: ${p.$clampLines};
    -webkit-box-orient: vertical;
  `
      : ""}
  ${(p) => (p.$isPlaceholder ? "opacity: 0.6; font-style: italic;" : "")}
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SeeMoreButton = styled(Button)`
  min-width: 0;
  padding: 0;
  font-size: 0.6875rem;
  letter-spacing: normal;
  text-transform: none;

  .MuiButton-endIcon svg {
    font-size: 1rem;
  }
`;

type Props = {
  label: string;
  value: string;
  fieldKey: keyof SideBySideComparisonSectionConfig;
  onHide?: (fieldKey: keyof SideBySideComparisonSectionConfig) => void;
  editMode?: boolean;
  hideLabel?: boolean;
  onEdit?: () => void;
  isEditingValue?: boolean;
  editValue?: string;
  onSaveValue?: (value: string) => void;
  onCancelEdit?: () => void;
  clampLines?: number;
  isPlaceholder?: boolean;
};

export const ComparisonPropertyFieldItem = (props: Props) => {
  const {
    label,
    value,
    fieldKey,
    onHide,
    editMode,
    hideLabel,
    onEdit,
    isEditingValue,
    editValue,
    onSaveValue,
    onCancelEdit,
    clampLines,
    isPlaceholder,
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const valueRef = useRef<HTMLElement | null>(null);
  const [isClamped, setIsClamped] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    if (!clampLines) return;
    setIsExpanded(false);
    const checkClamped = () => {
      const el = valueRef.current;
      setIsClamped(!!el && el.scrollHeight > el.clientHeight + 1);
    };
    checkClamped();
    window.addEventListener("resize", checkClamped);
    return () => window.removeEventListener("resize", checkClamped);
  }, [clampLines, value]);

  const [draftValue, setDraftValue] = useState(editValue ?? "");

  useEffect(() => {
    if (isEditingValue) setDraftValue(editValue ?? "");
  }, [isEditingValue, editValue]);

  const isInteractive = !!editMode && (!!onHide || !!onEdit) && !isEditingValue;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractive) {
      setAnchorEl(containerRef.current || event.currentTarget);
    }
  };

  const handleContainerKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (
      isInteractive &&
      event.target === event.currentTarget &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      setAnchorEl(containerRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleHide = useCallback(() => {
    if (onHide) {
      onHide(fieldKey);
      setAnchorEl(null);
    }
  }, [fieldKey, onHide]);

  // Deferred to the menu's onExited to avoid a focus race with autoFocus below.
  const pendingEditRef = useRef(false);

  const handleEdit = useCallback(() => {
    pendingEditRef.current = true;
    setAnchorEl(null);
  }, []);

  const handleMenuExited = useCallback(() => {
    if (pendingEditRef.current) {
      pendingEditRef.current = false;
      onEdit?.();
    }
  }, [onEdit]);

  const handleBlur = () => {
    onSaveValue?.(draftValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      setDraftValue(editValue ?? "");
      onCancelEdit?.();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSaveValue?.(draftValue);
    }
  };

  const handleSeeMore = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <>
      <FieldContainer
        ref={containerRef}
        $editMode={isInteractive}
        onClick={handleClick}
        onKeyDown={handleContainerKeyDown}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-controls={open ? `field-menu-${fieldKey}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        {!hideLabel && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {label}
          </Typography>
        )}
        {isEditingValue ? (
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={4}
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-input": {
                padding: "0.5rem !important",
                lineHeight: "1.25rem !important",
              },
            }}
            inputProps={{ maxLength: 500 }}
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
          />
        ) : clampLines ? (
          <ValueWrapper>
            <ValueText
              ref={valueRef}
              $clampLines={isExpanded ? undefined : clampLines}
              $isPlaceholder={isPlaceholder}
            >
              {value}
            </ValueText>
            <SeeMoreButton
              variant="text"
              size="small"
              sx={{ visibility: isClamped ? "visible" : "hidden" }}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleSeeMore}
            >
              {isExpanded ? "Show less" : "Show more"}
            </SeeMoreButton>
          </ValueWrapper>
        ) : (
          <Typography>{value}</Typography>
        )}
      </FieldContainer>
      <Menu
        id={`field-menu-${fieldKey}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        TransitionProps={{ onExited: handleMenuExited }}
      >
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" /> Edit
          </MenuItem>
        )}
        {onHide && (
          <MenuItem onClick={handleHide}>
            <VisibilityOffIcon fontSize="small" /> Hide
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
